import { produce } from 'immer';
import { asyncAction } from './async-action';
import { ScopedAsyncType } from './scoped-type';
import * as api from '../../lib/api';
import { getChartColors } from '../../services/chart-colors';

const initialState = {
  yields: null,
  yieldStats: {},
  loading: false,
  loadingApprovals: false,
};

const SLICE = 'yield';
const asyncType = ScopedAsyncType(SLICE);

const LOAD_GROWER_YIELD = asyncType('LOAD_GROWER_RAW_YIELD');
const LOAD_YIELD_STATS = asyncType('LOAD_YIELD_STATS');
const TOGGLE_YIELD_APPROVAL = asyncType('TOGGLE_YIELD_APPROVAL');

let currentYieldRequest = null;

export const selectors = {
  loading: (state) => state[SLICE].loading,
  loadingStats: (state) => state[SLICE].loadingStats,
  loadingApprovals: (state) => state[SLICE].loadingApprovals,
  rawYield: (state) => state[SLICE].yields,
  cleanYield: (state) =>
    state[SLICE].yields?.filter((result) =>
      ['Cleaned', 'Approved', 'Processing'].includes(result.status),
    ),
  yieldStats: (state) => state[SLICE].yieldStats,
  focusedFields: (state) => state[SLICE].focusedFields,
};

export const actions = {
  loadYieldData,
  loadYieldStats,
  toggleYieldApproval,
};

function loadYieldData(growerId, year) {
  if (currentYieldRequest) currentYieldRequest.abort();
  currentYieldRequest = new AbortController();

  return asyncAction(LOAD_GROWER_YIELD, () =>
    api.getYieldData(growerId, year, currentYieldRequest.signal),
  );
}

function loadYieldStats(growerId, aaid, crop, year) {
  return asyncAction(LOAD_YIELD_STATS, () =>
    api.getYieldStats(growerId, aaid, crop, year).then(async (yieldStats) => {
      const cArr = await getCropColorArray(crop, yieldStats).catch(() => []);
      return getFormattedGraph(yieldStats, cArr);
    }),
  );
}

async function getCropColorArray(crop) {
  let cArr = [];
  const cropLower = crop ? crop.toLowerCase() : '';

  switch (cropLower) {
    case 'corn':
      cArr = getChartColors('corn-yield');
      break;
    case 'soy':
      cArr = getChartColors('soy-yield');
      break;
    case 'wheat':
      cArr = getChartColors('wheat-yield');
      break;
    default:
      cArr = getChartColors('corn-yield');
  }

  return cArr;
}

function getFormattedGraph(yieldStats, cArr) {
  const formattedStats = {
    stats: {
      min: yieldStats.MinYield,
      max: yieldStats.MaxYield,
      avg: yieldStats.AverageYield,
      std: yieldStats.STDYield,
    },
    chart: {
      values: yieldStats.distribution.values
        .map((item) => ({
          label: item.Yield,
          value: item.Acres,
        }))
        .filter((item) => item.value > 0),
    },
  };

  const colors = formattedStats.chart.values.map((i) => {
    const color = i.label && cArr ? cArr[i.label] : null;
    return color
      ? `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3] / 255})`
      : 'rgba(0, 0, 0, .5)';
  });

  formattedStats.colors = colors;
  formattedStats.chart.values = formattedStats.chart.values.map(
    ({ label, value }) => ({
      label: label === 1000 ? '>300' : label,
      value,
    }),
  );

  return formattedStats;
}

function toggleYieldApproval(username, field, growerId, year) {
  return asyncAction(
    TOGGLE_YIELD_APPROVAL,
    () =>
      api.setYieldApproval(field.aaid, growerId, username, {
        year,
        crop: field.harvestedCrop,
        user: username,
        date: new Date().toISOString(),
        yieldapproved: !field.yieldapproved,
      }),
    { startPayload: field, failurePayload: field },
  );
}

const reducerHandlers = {
  [LOAD_GROWER_YIELD.start]: (state) => {
    state.yields = null;
    state.loading = true;
  },
  [LOAD_GROWER_YIELD.complete]: (state, payload) => {
    state.yields = payload;
    state.loading = false;
  },
  [LOAD_GROWER_YIELD.failure]: (state, error) => {
    if (error?.code === 'ERR_CANCELED') return;
    state.yields = [];
    state.loading = false;
  },
  [LOAD_YIELD_STATS.start]: (state) => {
    state.yieldStats = {};
    state.loadingStats = true;
  },
  [LOAD_YIELD_STATS.complete]: (state, payload) => {
    state.yieldStats = payload;
    state.loadingStats = false;
  },
  [LOAD_YIELD_STATS.failure]: (state) => {
    state.yieldStats = {};
    state.loadingStats = false;
  },
  [TOGGLE_YIELD_APPROVAL.start]: (state, payload) => {
    state.yields.forEach((y) => {
      if (y.aaid === payload.aaid && y.status === 'Cleaned') {
        y.yieldapproved = !payload.yieldapproved;
        y.status = 'Approved';
      } else if (y.aaid === payload.aaid) {
        y.yieldapproved = !payload.yieldapproved;
        y.status = 'Cleaned';
      }
    });

    state.loadingApprovals = true;
  },
  [TOGGLE_YIELD_APPROVAL.complete]: (state) => {
    state.loadingApprovals = false;
  },
  [TOGGLE_YIELD_APPROVAL.failure]: (state, payload) => {
    state.yields.forEach((y) => {
      if (y.aaid === payload.aaid) {
        y.yieldapproved = payload.yieldapproved;
        y.status = 'Cleaned';
      }
    });

    state.loadingApprovals = false;
  },
};

export const reducers = {
  [SLICE]: (state = initialState, { type, payload } = {}) => {
    if (!reducerHandlers[type]) return state;
    return produce(state, (draft) => reducerHandlers[type](draft, payload));
  },
};
